import {
  AfterViewInit,
  Component,
  OnInit,
  ElementRef,
  EventEmitter,
  Output,
  computed,
  inject,
  input,
  signal,
  viewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { UiButtonComponent } from '@ui';
import { IntelligenceModels } from '@models/intelligence.model';

/** Starter chips, shown only while the thread is still empty. */
const AI_COMPOSER_SUGGESTIONS: readonly IntelligenceModels.Chat.ComposerSuggestion[] =
  [
    {
      labelKey: 'SEARCH_GLOBAL_MODAL.AI_COMPOSER.SUGGESTION_HEALTH_LABEL',
      promptKey: 'SEARCH_GLOBAL_MODAL.AI_COMPOSER.SUGGESTION_HEALTH_PROMPT',
    },
    {
      labelKey: 'SEARCH_GLOBAL_MODAL.AI_COMPOSER.SUGGESTION_ALERTS_LABEL',
      promptKey: 'SEARCH_GLOBAL_MODAL.AI_COMPOSER.SUGGESTION_ALERTS_PROMPT',
    },
    {
      labelKey: 'SEARCH_GLOBAL_MODAL.AI_COMPOSER.SUGGESTION_ARCHIVES_LABEL',
      promptKey: 'SEARCH_GLOBAL_MODAL.AI_COMPOSER.SUGGESTION_ARCHIVES_PROMPT',
    },
  ];

const MIN_ROWS = 1;
const MAX_ROWS = 5;
const TOP_BOTTOM_BORDER_WIDTH_PX = 2;
const LINE_HEIGHT_PX = 20;
const VERTICAL_PADDING_PX = 10;
const FLIP_DURATION_MS = 240;
const FLIP_EASING = 'cubic-bezier(.2, .8, .25, 1)';

/**
 * Bottom-docked prompt composer for the Cmd+K AI chat shell.
 *
 * Deliberately a sibling of `copilot-chat` rather than its `inputComponent`:
 * routing input through CopilotKit's input container re-enables its
 * ResizeObserver-driven layout, which the panel's `::ng-deep` overrides
 * intentionally neutralize, and moves scroll ownership away from the result
 * column that every result panel depends on.
 */
@Component({
  selector: 'app-ai-composer',
  standalone: true,
  imports: [CommonModule, FormsModule, TranslatePipe, UiButtonComponent],
  templateUrl: './ai-composer.component.html',
  styleUrls: ['./ai-composer.component.scss'],
})
export class AiComposerComponent implements OnInit, AfterViewInit {
  /**
   * Viewport `top` of the search input the composer animates away from, for the
   * FLIP entrance. `null` skips the animation.
   */
  public readonly flipFromTop = input<number | null>(null);

  /** Prefilled text carried over from the search input (e.g. text typed after `?`). */
  public readonly initialValue = input<string>('');

  /** True while the agent is streaming — submission is blocked. */
  public readonly busy = input<boolean>(false);

  /** Starter chips are only useful before the first prompt. */
  public readonly showSuggestions = input<boolean>(false);

  @Output() public readonly promptSubmit = new EventEmitter<string>();

  /** Emitted when the user aborts the streaming turn from the Stop control. */
  @Output() public readonly stopRequest = new EventEmitter<void>();

  protected readonly inputRef =
    viewChild<ElementRef<HTMLTextAreaElement>>('composerInput');

  protected readonly value = signal<string>('');
  protected readonly minRows: number = MIN_ROWS;
  protected readonly suggestions: readonly IntelligenceModels.Chat.ComposerSuggestion[] =
    AI_COMPOSER_SUGGESTIONS;

  protected readonly canSubmit = computed<boolean>(
    () => this.value().trim().length > 0 && !this.busy(),
  );

  private readonly elementRef: ElementRef<HTMLElement> = inject(ElementRef);
  private readonly translateService: TranslateService =
    inject(TranslateService);

  public ngOnInit(): void {
    // Set before the first render so the seeded text is never a
    // changed-after-checked update.
    this.value.set(this.initialValue());
  }

  public ngAfterViewInit(): void {
    this.runFlipEntrance();
    // Focus after the FLIP starts so the caret lands in the composer, not the
    // now-hidden palette input.
    setTimeout(() => this.focusInput(), 0);
  }

  public focusInput(): void {
    this.inputRef()?.nativeElement?.focus();
  }

  protected onValueChange(next: string): void {
    this.value.set(next);
    this.autoGrow();
  }

  protected onKeyDown(event: KeyboardEvent): void {
    if (event.key !== 'Enter' || event.shiftKey) {
      return;
    }
    event.preventDefault();
    this.submit();
  }

  /**
   * Abort the running turn. The send button becomes this control while the
   * agent streams, so Enter/Shift+Enter keep their plain text behaviour.
   */
  protected stop(): void {
    this.stopRequest.emit();
  }

  protected submit(): void {
    if (!this.canSubmit()) {
      return;
    }
    const prompt = this.value().trim();
    this.value.set('');
    // Shrink back only after the cleared value has reached the DOM — scrollHeight
    // still reports the old content in this tick.
    setTimeout(() => this.autoGrow(), 0);
    this.promptSubmit.emit(prompt);
  }

  protected useSuggestion(
    suggestion: IntelligenceModels.Chat.ComposerSuggestion,
  ): void {
    if (this.busy()) {
      return;
    }
    this.value.set(this.translateService.instant(suggestion.promptKey));
    this.submit();
  }

  /** Grow the textarea with its content, capped at MAX_ROWS. */
  private autoGrow(): void {
    const element = this.inputRef()?.nativeElement;
    if (!element) {
      return;
    }
    element.style.height = 'auto';
    const maxHeight = LINE_HEIGHT_PX * MAX_ROWS + VERTICAL_PADDING_PX;
    element.style.height = `${Math.min(element.scrollHeight + TOP_BOTTOM_BORDER_WIDTH_PX, maxHeight)}px`;
  }

  /**
   * FLIP the composer from the search input's old position to its docked one.
   * Transform/opacity only — animating height or width here would resize the
   * result column mid-flight, and panels that measure their canvas once (chart,
   * wall camera) would bake in the wrong size.
   */
  private runFlipEntrance(): void {
    const from = this.flipFromTop();
    const host = this.elementRef.nativeElement;
    if (from == null || this.prefersReducedMotion()) {
      return;
    }

    const delta = from - host.getBoundingClientRect().top;
    if (!delta) {
      return;
    }

    host.style.transition = 'none';
    host.style.transform = `translateY(${delta}px)`;
    host.style.opacity = '0';

    requestAnimationFrame(() => {
      host.style.transition = `transform ${FLIP_DURATION_MS}ms ${FLIP_EASING}, opacity ${FLIP_DURATION_MS}ms ${FLIP_EASING}`;
      host.style.transform = 'translateY(0)';
      host.style.opacity = '1';
      // Drop the inline transition so later layout shifts are not animated.
      setTimeout(() => {
        host.style.transition = '';
        host.style.transform = '';
        host.style.opacity = '';
      }, FLIP_DURATION_MS);
    });
  }

  private prefersReducedMotion(): boolean {
    return (
      typeof window !== 'undefined' &&
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches
    );
  }
}
